import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { getOrCreateDeviceId } from "../../../utils/deviceId";
import { logoutCurrentSession } from "../../../utils/sessionLogout";
import "./securityLogs.css";

const TAB_DEVICES = "devices";
const TAB_SESSIONS = "sessions";

const DEVICE_COLUMNS = ["Device", "Status", "Last active", "IP", "Location", "Actions"];
const SESSION_COLUMNS = ["Device / client", "Last active", "Status", "IP", "Location", "Actions"];

const pad2 = (n) => String(n).padStart(2, "0");

function formatAbsoluteTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

function formatRelativeTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  let diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 0) diffSec = 0;
  if (diffSec < 60) return "just now";
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) {
    return remMins > 0 ? `${hours} hours ${remMins} min ago` : `${hours} hours ago`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days} days ${remHours} hours ago` : `${days} days ago`;
}

function formatTimeCell(ts) {
  const abs = formatAbsoluteTime(ts);
  if (abs === "—") return abs;
  const rel = formatRelativeTime(ts);
  if (!rel) return abs;
  return (
    <span className="slg-timeCell">
      <span className="slg-timeAbs">{abs}</span>
      <span className="slg-timeRel">{rel}</span>
    </span>
  );
}

function formatLocation(item) {
  const loc = item?.location || {};
  if (typeof loc === "string" && loc.trim()) return loc;
  if (loc.country) return loc.country;
  if (loc.city && loc.region) return `${loc.city}, ${loc.region}`;
  if (loc.city) return loc.city;
  if (loc.region) return loc.region;
  return "—";
}

function statusLabel(status) {
  const s = String(status || "").toUpperCase();
  if (s === "TRUSTED") return "Trusted";
  if (s === "NEW") return "New / untrusted";
  if (s === "BLOCKED_TEMP") return "Blocked (temp)";
  if (s === "BLOCKED_PERM") return "Blocked";
  if (s === "ACTIVE") return "Active";
  return s || "—";
}

function isBlockedStatus(status) {
  const s = String(status || "").toUpperCase();
  return s === "BLOCKED_TEMP" || s === "BLOCKED_PERM";
}

function deviceLabel(d) {
  const name = d.device_name || [d.browser, d.os].filter(Boolean).join(" on ") || d.platform;
  return name || d.device_id || "Device";
}

function sessionLabel(s) {
  const parts = [s.device_name, s.browser, s.os, s.client_type, s.platform].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return s.device_id || s.session_id || "Session";
}

function tabFromSearch(searchParams) {
  const raw = String(searchParams.get("tab") || "").toLowerCase();
  if (raw === TAB_SESSIONS || raw === "session" || raw === "active") return TAB_SESSIONS;
  return TAB_DEVICES;
}

/**
 * Authorized devices + active sessions (Security Logs–style tabs).
 * Route: /user_profile/security/authorizedDevices[?tab=devices|sessions]
 */
const AuthorizedDevices = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => tabFromSearch(searchParams));

  const [devices, setDevices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentDeviceId, setCurrentDeviceId] = useState(() => getOrCreateDeviceId());
  const [currentSessionId, setCurrentSessionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    setTab(tabFromSearch(searchParams));
  }, [searchParams]);

  const switchTab = (next) => {
    setTab(next);
    setSearchParams(next === TAB_SESSIONS ? { tab: TAB_SESSIONS } : {}, { replace: true });
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const [devRes, sessRes] = await Promise.all([AuthService.listDevices(), AuthService.listSessions()]);
      if (devRes?.success) {
        const list = Array.isArray(devRes?.data?.devices) ? devRes.data.devices : [];
        setDevices(list);
        if (devRes?.data?.current_device_id) {
          setCurrentDeviceId(String(devRes.data.current_device_id));
        }
      } else if (devRes?.message) {
        alertErrorMessage(devRes.message);
      }
      if (sessRes?.success) {
        const list = Array.isArray(sessRes?.data?.sessions) ? sessRes.data.sessions : [];
        setSessions(list);
        if (sessRes?.data?.current_session_id) {
          setCurrentSessionId(String(sessRes.data.current_session_id));
        }
      } else if (sessRes?.message) {
        alertErrorMessage(sessRes.message);
      }
    } catch (e) {
      alertErrorMessage(e?.message || "Failed to load devices");
    } finally {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const otherSessionCount = useMemo(
    () => sessions.filter((s) => !(s.is_current || s.session_id === currentSessionId)).length,
    [sessions, currentSessionId]
  );

  const runWithBusy = async (id, fn) => {
    setBusyId(id);
    try {
      await fn();
    } finally {
      setBusyId("");
    }
  };

  const runDeviceAction = async (deviceId, successMsg, apiFn) => {
    await runWithBusy(deviceId, async () => {
      const res = await apiFn();
      if (res?.success) {
        alertSuccessMessage(res.message || successMsg);
        await loadAll();
      } else {
        alertErrorMessage(res?.message || "Action failed");
      }
    });
  };

  const handleTrust = (deviceId) =>
    runDeviceAction(deviceId, "Device trusted", () => AuthService.trustDevice(deviceId));

  const handleUntrust = (deviceId) =>
    runDeviceAction(deviceId, "Device trust removed", () => AuthService.untrustDevice(deviceId));

  const handleBindDeviceIp = (deviceId) =>
    runDeviceAction(deviceId, "Device bound to this IP", () => AuthService.bindDeviceIp(deviceId));

  const handleUnbindDeviceIp = (deviceId) =>
    runDeviceAction(deviceId, "Device IP unbound", () => AuthService.unbindDeviceIp(deviceId));

  const handleBlockDevice = (deviceId, opts = {}) =>
    runDeviceAction(deviceId, opts.permanent ? "Device blocked" : "Device blocked for 24 hours", () =>
      AuthService.blockDevice(deviceId, opts)
    );

  const handleUnblockDevice = (deviceId) =>
    runDeviceAction(deviceId, "Device unblocked", () => AuthService.unblockDevice(deviceId));

  const handleRemoveDevice = (deviceId) =>
    runDeviceAction(deviceId, "Device removed", () => AuthService.removeDevice(deviceId));

  const handleBindSessionIp = (sessionId) =>
    runDeviceAction(sessionId, "Session bound to this IP", () => AuthService.bindSessionIp(sessionId));

  const handleUnbindSessionIp = (sessionId) =>
    runDeviceAction(sessionId, "Session IP unbound", () => AuthService.unbindSessionIp(sessionId));

  const handleRevokeOne = async (sessionId) => {
    await runWithBusy(sessionId, async () => {
      const res = await AuthService.revokeSession(sessionId);
      if (res?.success) {
        alertSuccessMessage(res.message || "Session ended");
        await loadAll();
      } else {
        alertErrorMessage(res?.message || "Failed to end session");
      }
    });
  };

  const handleRevokeOthers = async () => {
    await runWithBusy("revoke-others", async () => {
      const res = await AuthService.revokeOtherSessions();
      if (res?.success) {
        alertSuccessMessage(res.message || "Other sessions ended");
        await loadAll();
      } else {
        alertErrorMessage(res?.message || "Failed to end other sessions");
      }
    });
  };

  const handleRevokeAll = async () => {
    await runWithBusy("revoke-all", async () => {
      const res = await AuthService.revokeAllSessions();
      if (res?.success) {
        alertSuccessMessage(res.message || "Logged out everywhere");
        await logoutCurrentSession();
        navigate("/login", { replace: true });
      } else {
        alertErrorMessage(res?.message || "Failed to log out all sessions");
      }
    });
  };

  const columns = tab === TAB_DEVICES ? DEVICE_COLUMNS : SESSION_COLUMNS;
  const colCount = columns.length;
  const rowsEmpty = tab === TAB_DEVICES ? devices.length === 0 : sessions.length === 0;
  const isEmpty = !loading && rowsEmpty;

  const renderDeviceActions = (d) => {
    const id = d.device_id;
    const isCurrent = id && String(id) === String(currentDeviceId);
    const trusted = String(d.status || "").toUpperCase() === "TRUSTED";
    const blocked = isBlockedStatus(d.status);
    const busy = busyId === id;
    return (
      <div className="adv-actions">
        {!blocked && trusted ? (
          <button type="button" className="adv-btn" disabled={busy || !!busyId} onClick={() => void handleUntrust(id)}>
            Untrust
          </button>
        ) : null}
        {!blocked && !trusted ? (
          <button
            type="button"
            className="adv-btn adv-btn--primary"
            disabled={busy || !!busyId}
            onClick={() => void handleTrust(id)}
          >
            Trust
          </button>
        ) : null}
        {d.ip_bound ? (
          <button
            type="button"
            className="adv-btn"
            disabled={busy || !!busyId}
            onClick={() => void handleUnbindDeviceIp(id)}
          >
            Unbind IP
          </button>
        ) : (
          <button
            type="button"
            className="adv-btn"
            disabled={busy || !!busyId || blocked}
            onClick={() => void handleBindDeviceIp(id)}
          >
            Bind IP
          </button>
        )}
        {blocked ? (
          <button
            type="button"
            className="adv-btn adv-btn--primary"
            disabled={busy || !!busyId}
            onClick={() => void handleUnblockDevice(id)}
          >
            Unblock
          </button>
        ) : !isCurrent ? (
          <>
            <button
              type="button"
              className="adv-btn"
              disabled={busy || !!busyId}
              onClick={() => void handleBlockDevice(id, { durationHours: 24 })}
            >
              Block 24h
            </button>
            <button
              type="button"
              className="adv-btn adv-btn--danger"
              disabled={busy || !!busyId}
              onClick={() => void handleBlockDevice(id, { permanent: true })}
            >
              Block
            </button>
          </>
        ) : null}
        {!isCurrent ? (
          <button
            type="button"
            className="adv-btn adv-btn--danger"
            disabled={busy || !!busyId}
            onClick={() => void handleRemoveDevice(id)}
          >
            Remove
          </button>
        ) : null}
      </div>
    );
  };

  const renderSessionActions = (s) => {
    const id = s.session_id;
    const isCurrent = s.is_current || id === currentSessionId;
    const busy = busyId === id;
    if (isCurrent) {
      return (
        <div className="adv-actions">
          {s.ip_bound ? (
            <button
              type="button"
              className="adv-btn"
              disabled={busy || !!busyId}
              onClick={() => void handleUnbindSessionIp(id)}
            >
              Unbind IP
            </button>
          ) : (
            <button
              type="button"
              className="adv-btn adv-btn--primary"
              disabled={busy || !!busyId}
              onClick={() => void handleBindSessionIp(id)}
            >
              Bind IP
            </button>
          )}
          <span className="adv-muted">Current</span>
        </div>
      );
    }
    return (
      <div className="adv-actions">
        <button
          type="button"
          className="adv-btn adv-btn--danger"
          disabled={busy || !!busyId}
          onClick={() => void handleRevokeOne(id)}
        >
          End session
        </button>
      </div>
    );
  };

  return (
    <main className="slg-page" aria-labelledby="adv-title">
      <nav className="slg-page__crumbs" aria-label="Breadcrumb">
        <ol className="slg-page__crumbList">
          <li className="slg-page__crumbItem">
            <button type="button" className="slg-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="slg-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="slg-page__crumbItem slg-page__crumbItem--active" aria-current="page">
            Authorized Devices
          </li>
        </ol>
      </nav>

      <div className="security_section_bl">
        <h1 id="adv-title" className="slg-page__title">
          Authorized Devices
        </h1>
        <p className="slg-page__sub">
          Manages and recognizes trusted devices for secure account access. Helps prevent unauthorized logins by allowing
          access only from approved devices.
        </p>

        <div className="slg-tabs" role="tablist" aria-label="Devices or sessions">
          <button
            type="button"
            role="tab"
            aria-selected={tab === TAB_DEVICES}
            id="adv-tab-devices"
            aria-controls="adv-panel"
            className={`slg-tab${tab === TAB_DEVICES ? " is-active" : ""}`}
            onClick={() => switchTab(TAB_DEVICES)}
          >
            Trusted Devices
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === TAB_SESSIONS}
            id="adv-tab-sessions"
            aria-controls="adv-panel"
            className={`slg-tab${tab === TAB_SESSIONS ? " is-active" : ""}`}
            onClick={() => switchTab(TAB_SESSIONS)}
          >
            Active Sessions
          </button>
        </div>

        {tab === TAB_SESSIONS ? (
          <div className="adv-sessionCard adv-sessionCard--compact">
            <div className="adv-sessionCard__main">
              <strong>Session controls</strong>
              <span className="adv-sessionCard__meta">
                End remote logins, keep only this device, or force logout everywhere.
              </span>
            </div>
            <div className="adv-actions">
              <button
                type="button"
                className="adv-btn"
                disabled={loading || !!busyId || otherSessionCount === 0}
                onClick={() => void handleRevokeOthers()}
              >
                Log out other sessions{otherSessionCount > 0 ? ` (${otherSessionCount})` : ""}
              </button>
              <button
                type="button"
                className="adv-btn adv-btn--danger"
                disabled={loading || !!busyId}
                onClick={() => void handleRevokeAll()}
              >
                Log out all
              </button>
              <button type="button" className="adv-btn" disabled={loading || !!busyId} onClick={() => void loadAll()}>
                Refresh
              </button>
            </div>
          </div>
        ) : (
          <div className="adv-sessionCard adv-sessionCard--compact">
            <div className="adv-sessionCard__main">
              <strong>Device controls</strong>
              <span className="adv-sessionCard__meta">
                Trust, block, or bind IP on recognized devices. Bound IP changes require sign-in again.
              </span>
            </div>
            <div className="adv-actions">
              <button type="button" className="adv-btn" disabled={loading || !!busyId} onClick={() => void loadAll()}>
                Refresh
              </button>
            </div>
          </div>
        )}

        <div
          id="adv-panel"
          role="tabpanel"
          aria-labelledby={tab === TAB_DEVICES ? "adv-tab-devices" : "adv-tab-sessions"}
          className="slg-tableWrap slg-tableWrap--mobileCards"
        >
          <div className="slg-tableScroll">
            <table className="slg-table">
              <thead>
                <tr>
                  {columns.map((label) => (
                    <th key={label} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="slg-table__emptyRow">
                    <td colSpan={colCount}>
                      <div className="slg-empty" aria-live="polite">
                        <p className="slg-empty__text">
                          {tab === TAB_DEVICES ? "Loading devices…" : "Loading sessions…"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : isEmpty ? (
                  <tr className="slg-table__emptyRow">
                    <td colSpan={colCount}>
                      <div className="slg-empty" aria-live="polite">
                        <img
                          src="/images/no-data.svg"
                          alt=""
                          width={120}
                          height={144}
                          className="spot_orders_empty_telescope"
                        />
                        <p className="slg-empty__text">
                          {tab === TAB_DEVICES ? "No devices yet" : "No active sessions"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : tab === TAB_DEVICES ? (
                  devices.map((d) => {
                    const id = d.device_id;
                    const isCurrent = id && String(id) === String(currentDeviceId);
                    const trusted = String(d.status || "").toUpperCase() === "TRUSTED";
                    const blocked = isBlockedStatus(d.status);
                    return (
                      <tr key={id}>
                        <td data-label="Device">
                          <span className="slg-cardVal">
                            {deviceLabel(d)}
                            {isCurrent ? " (this device)" : ""}
                            <br />
                            <span className="adv-muted">
                              {[d.os, d.browser, d.platform].filter(Boolean).join(" · ") || id}
                            </span>
                          </span>
                        </td>
                        <td data-label="Status">
                          <span className={`adv-badge${trusted ? " adv-badge--ok" : ""}${blocked ? " adv-badge--danger" : ""}`}>
                            {statusLabel(d.status)}
                          </span>
                        </td>
                        <td data-label="Last active">
                          <span className="slg-cardVal">{formatTimeCell(d.last_seen_at)}</span>
                        </td>
                        <td data-label="IP">
                          <span className="slg-cardVal">{d.last_ip || "—"}</span>
                        </td>
                        <td data-label="Location">
                          <span className="slg-cardVal">{formatLocation(d)}</span>
                        </td>
                        <td data-label="Actions">{renderDeviceActions(d)}</td>
                      </tr>
                    );
                  })
                ) : (
                  sessions.map((s) => {
                    const id = s.session_id;
                    const isCurrent = s.is_current || id === currentSessionId;
                    return (
                      <tr key={id}>
                        <td data-label="Device / client">
                          <span className="slg-cardVal">
                            {sessionLabel(s)}
                            {isCurrent ? " (this device)" : ""}
                            <br />
                            <span className="adv-muted">{s.device_id || id}</span>
                          </span>
                        </td>
                        <td data-label="Last active">
                          <span className="slg-cardVal">
                            {formatTimeCell(s.last_seen_at || s.updated_at || s.created_at)}
                          </span>
                        </td>
                        <td data-label="Status">
                          <span className={`adv-badge${isCurrent ? " adv-badge--ok" : ""}`}>
                            {isCurrent ? "Current" : statusLabel(s.status || "ACTIVE")}
                          </span>
                        </td>
                        <td data-label="IP">
                          <span className="slg-cardVal">{s.ip || s.last_ip || "—"}</span>
                        </td>
                        <td data-label="Location">
                          <span className="slg-cardVal">{formatLocation(s)}</span>
                        </td>
                        <td data-label="Actions">{renderSessionActions(s)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AuthorizedDevices;
